import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';

const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    {children}
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>{error}</p>}
  </div>
);

const inputStyle = (hasError) => ({
  width: '100%', padding: '8px 12px', border: `1.5px solid ${hasError ? '#EF4444' : '#E5E7EB'}`,
  borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none',
  fontFamily: 'inherit', transition: 'border-color 0.15s',
});

const SectionCard = ({ title, subtitle, children }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' }}>
    <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{title}</span>
      {subtitle && <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 8 }}>{subtitle}</span>}
    </div>
    <div style={{ padding: '18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
      {children}
    </div>
  </div>
);

const ClientEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});
  const [clientName, setClientName] = useState('');
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', reason: '',
    contact_name: '', contact_phone: '', contact_email: '', contact_role: '',
    assigned_user_id: '', status: 'active',
  });

  useEffect(() => {
    Promise.all([api.get(`/clients/${id}`), api.get('/users')])
      .then(([clientRes, usersRes]) => {
        const c = clientRes.data;
        setClientName(c.name);
        setForm({
          name: c.name || '', phone: c.phone || '', email: c.email || '',
          address: c.address || '', reason: c.reason || '',
          contact_name: c.contact_name || '', contact_phone: c.contact_phone || '',
          contact_email: c.contact_email || '', contact_role: c.contact_role || '',
          assigned_user_id: c.assigned_user_id || '', status: c.status || 'active',
        });
        setUsers(usersRes.data);
      }).finally(() => setFetching(false));
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const focusStyle = (e) => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
  const blurStyle  = (e, hasError) => { e.target.style.borderColor = hasError ? '#EF4444' : '#E5E7EB'; e.target.style.boxShadow = 'none'; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/clients/${id}`, form);
      navigate(`/clients/${id}`);
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
    } finally { setLoading(false); }
  };

  if (fetching) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        <Link to="/clients" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Clientes</Link>
        <span>›</span>
        <Link to={`/clients/${id}`} style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>{clientName}</Link>
        <span>›</span>
        <span style={{ color: '#4B5563' }}>Editar</span>
      </div>

      <PageHeader
        title="Editar Cliente"
        subtitle={`Modificando datos de ${clientName}`}
        actions={
          <Link to={`/clients/${id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Regresar
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <SectionCard title="Datos del Cliente">
          <Field label="Nombre" required error={errors.name?.[0]}>
            <input type="text" name="name" value={form.name} onChange={handleChange} required
              style={inputStyle(!!errors.name)} onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.name)} />
          </Field>
          <Field label="Teléfono">
            <input type="text" name="phone" value={form.phone} onChange={handleChange}
              style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
          </Field>
          <Field label="Correo electrónico" error={errors.email?.[0]}>
            <input type="email" name="email" value={form.email} onChange={handleChange}
              style={inputStyle(!!errors.email)} onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.email)} />
          </Field>
          <Field label="Estado">
            <select name="status" value={form.status} onChange={handleChange} style={{ ...inputStyle(false), cursor: 'pointer' }}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="completed">Completado</option>
            </select>
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Dirección">
              <input type="text" name="address" value={form.address} onChange={handleChange}
                style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
            </Field>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Motivo" required error={errors.reason?.[0]}>
              <textarea name="reason" rows={3} value={form.reason} onChange={handleChange} required
                style={{ ...inputStyle(!!errors.reason), resize: 'vertical' }} onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.reason)} />
            </Field>
          </div>
          <Field label="Responsable" required error={errors.assigned_user_id?.[0]}>
            <select name="assigned_user_id" value={form.assigned_user_id} onChange={handleChange} required
              style={{ ...inputStyle(!!errors.assigned_user_id), cursor: 'pointer' }}>
              <option value="">Seleccionar...</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        </SectionCard>

        <SectionCard title="Contacto" subtitle="Broker, gestor u otro intermediario — opcional">
          <Field label="Nombre del contacto">
            <input type="text" name="contact_name" value={form.contact_name} onChange={handleChange}
              style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
          </Field>
          <Field label="Rol / Cargo">
            <input type="text" name="contact_role" placeholder="ej. Broker, Gestor, Apoderado" value={form.contact_role} onChange={handleChange}
              style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
          </Field>
          <Field label="Teléfono del contacto">
            <input type="text" name="contact_phone" value={form.contact_phone} onChange={handleChange}
              style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
          </Field>
          <Field label="Correo del contacto">
            <input type="email" name="contact_email" value={form.contact_email} onChange={handleChange}
              style={inputStyle(false)} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
          </Field>
        </SectionCard>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" disabled={loading}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <Link to={`/clients/${id}`} style={{ padding: '9px 22px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ClientEdit;
